/**
 * KanbanCard Component
 *
 * Componente per una singola card del task nel Kanban board
 * Supporta drag & drop e mostra informazioni del task
 * Versione migliorata con tag colore utente e pulsante elimina
 */

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Paper,
  Text,
  Badge,
  Group,
  Stack,
  Progress,
  Avatar,
  Tooltip,
  ActionIcon,
  Box,
  Menu,
} from '@mantine/core';
import {
  IconCalendar,
  IconCheckbox,
  IconClock,
  IconFlag,
  IconUser,
  IconGripVertical,
  IconAlertTriangle,
  IconTrash,
  IconDotsVertical,
  IconEdit,
  IconCopy,
} from '@tabler/icons-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface KanbanCardProps {
  task: {
    id: number;
    title: string;
    name?: string; // Fallback per compatibilita
    description?: string;
    status: string;
    priority: string; // Backend usa stringhe: "Low", "Medium", "High", "Critical"
    completionPercentage?: number;
    checklistItemsCount?: number;
    completedChecklistItemsCount?: number;
    isOverdue?: boolean;
    dueDate?: Date | string;
    internalDueDate?: Date | string; // Data scadenza interna
    assignedToUser?: {
      id: number;
      fullName: string;
      email: string;
      colorTag?: string; // Colore tag utente
    };
    assignedToUserId?: number;
    assignedToUserName?: string;
    assignedToUserColorTag?: string; // Colore tag utente (alternativo)
    tag?: string;
    // Altre proprieta dal backend
    requestId?: number;
    createdAt?: string;
    updatedAt?: string;
    commentsCount?: number;
    attachmentsCount?: number;
  };
  onClick: () => void;
  onDelete?: (taskId: number) => void;
  onEdit?: (taskId: number) => void;
  onDuplicate?: (taskId: number) => void;
  isDragging?: boolean;
  showActions?: boolean;
}

const KanbanCard: React.FC<KanbanCardProps> = ({
  task,
  onClick,
  onDelete,
  onEdit,
  onDuplicate,
  isDragging = false,
  showActions = true,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: String(task.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || isSortableDragging ? 0.5 : 1,
  };

  // Funzioni helper per i colori
  const getPriorityColor = (priority: string): string => {
    if (!priority || typeof priority !== 'string') return 'gray';
    switch (priority.toLowerCase()) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'orange';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  const getPriorityLabel = (priority: string): string => {
    if (!priority || typeof priority !== 'string') return 'Non definita';
    switch (priority.toLowerCase()) {
      case 'low': return 'Bassa';
      case 'medium': return 'Media';
      case 'high': return 'Alta';
      case 'critical': return 'Critica';
      default: return 'Non definita';
    }
  };

  const getStatusColor = (status: string): string => {
    if (!status || typeof status !== 'string') return 'gray';
    switch (status.toLowerCase()) {
      case 'todo': 
      case 'ideas': 
        return 'gray';
      case 'doing': 
      case 'inprogress': 
        return 'blue';
      case 'testing': 
        return 'orange';
      case 'done': 
      case 'completed': 
        return 'green';
      default: 
        return 'gray';
    }
  };

  const checklistProgress = task.checklistItemsCount && task.checklistItemsCount > 0
    ? ((task.completedChecklistItemsCount || 0) / task.checklistItemsCount) * 100
    : 0;

  // Ottieni il colore tag dell'utente assegnato
  const userColorTag = task.assignedToUser?.colorTag || task.assignedToUserColorTag;
  const userName = task.assignedToUser?.fullName || task.assignedToUserName;

  // Determina la data di scadenza da mostrare (preferisce internalDueDate)
  const displayDueDate = task.internalDueDate || task.dueDate;

  // Handler per stopPropagation negli action menu
  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...attributes}
      p="md"
      shadow="sm"
      withBorder
      onClick={onClick}
      className="cursor-pointer hover:shadow-md transition-shadow"
    >
      <Stack gap="sm">
        {/* Header con grip, titolo e azioni */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
            {/* Grip per drag */}
            <ActionIcon
              variant="subtle"
              size="sm"
              {...listeners}
              style={{ cursor: 'grab' }}
            >
              <IconGripVertical size={14} />
            </ActionIcon>

            {/* Tag colore utente */}
            {userColorTag && (
              <Box
                style={{
                  width: 4,
                  height: 16,
                  backgroundColor: userColorTag,
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              />
            )}

            {/* Titolo del task */}
            <Tooltip
              label={task.title || task.name}
              multiline
              w={300}
              withArrow
              disabled={!(task.title || task.name) || (task.title || task.name || '').length < 30}
            >
              <Text
                fw={500}
                size="sm"
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {task.title || task.name}
              </Text>
            </Tooltip>
          </Group>

          <Group gap={4} wrap="nowrap">
            {/* Badge priorita */}
            <Badge
              size="xs"
              color={getPriorityColor(task.priority)}
              variant="filled"
              style={{ color: 'white' }}
            >
              {getPriorityLabel(task.priority)}
            </Badge>

            {/* Menu azioni */}
            {showActions && (onDelete || onEdit || onDuplicate) && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={handleMenuClick}
                  >
                    <IconDotsVertical size={14} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown onClick={handleMenuClick}>
                  {onEdit && (
                    <Menu.Item
                      leftSection={<IconEdit size={14} />}
                      onClick={() => onEdit(task.id)}
                    >
                      Modifica
                    </Menu.Item>
                  )}
                  {onDuplicate && (
                    <Menu.Item
                      leftSection={<IconCopy size={14} />}
                      onClick={() => onDuplicate(task.id)}
                    >
                      Duplica
                    </Menu.Item>
                  )}
                  {onDelete && (
                    <>
                      <Menu.Divider />
                      <Menu.Item
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={() => onDelete(task.id)}
                      >
                        Elimina
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>

        {/* Descrizione (se presente) */}
        {task.description && (
          <Tooltip
            label={task.description}
            multiline
            w={350}
            withArrow
            disabled={!task.description || task.description.length < 50}
          >
            <Text
              size="xs"
              c="dimmed"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {task.description}
            </Text>
          </Tooltip>
        )}

        {/* Progresso checklist */}
        {/* {console.log(task)}
        {task.checklistItemsCount && task.checklistItemsCount > 0 && (
          <Box>
            <Group justify="space-between" mb={4}>
              <Group gap={4}>
                <IconCheckbox size={12} />
                <Text size="xs" c="dimmed">
                  {task.completedChecklistItemsCount || 0}/{task.checklistItemsCount} completati
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {Math.round(checklistProgress)}% 
              </Text>
            </Group>
            <Progress
              value={checklistProgress}
              size="xs"
              color={checklistProgress === 100 ? 'green' : 'blue'}
            />
            
          </Box>
        )} */}

        {/* Info aggiuntive */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            {/* Data di scadenza */}
            {displayDueDate && (
              <Tooltip
                label={`Scadenza: ${format(
                  typeof displayDueDate === 'string' ? new Date(displayDueDate) : displayDueDate,
                  'dd/MM/yyyy',
                  { locale: it }
                )}${task.internalDueDate ? ' (interna)' : ''}`}
              >
                <Group gap={2}>
                  <IconCalendar
                    size={12}
                    color={task.isOverdue ? '#fa5252' : '#868e96'}
                  />
                  <Text
                    size="xs"
                    c={task.isOverdue ? 'red' : 'dimmed'}
                  >
                    {format(
                      typeof displayDueDate === 'string' ? new Date(displayDueDate) : displayDueDate,
                      'dd/MM',
                      { locale: it }
                    )}
                  </Text>
                  {task.isOverdue && (
                    <IconAlertTriangle size={12} color="#fa5252" />
                  )}
                </Group>
              </Tooltip>
            )}

            {/* Tag */}
            {task.tag && (
              <Badge size="xs" variant="outline" color="gray">
                {task.tag}
              </Badge>
            )}
          </Group>

          {/* Avatar utente assegnato con colore tag */}
          {userName && (
            <Tooltip label={`Assegnato a: ${userName}`}>
              <Avatar
                size="sm"
                name={userName}
                color={userColorTag || 'blue'}
                style={userColorTag ? {
                  border: `2px solid ${userColorTag}`,
                } : undefined}
              >
                {userName
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)
                }
              </Avatar>
            </Tooltip>
          )}
        </Group>

        {/* Badge di stato (opzionale) */}
        {task.isOverdue && (
          <Badge
            size="xs"
            color="red"
            variant="filled"
            style={{ color: 'white' }}
            leftSection={<IconAlertTriangle size={10} />}
          >
            In ritardo
          </Badge>
        )}
      </Stack>
    </Paper>
  );
};

export default KanbanCard;